import { uniq } from 'es-toolkit';
import React from 'react';
import { Plugin, Template, TemplatePlaceholder } from '@devexpress/dx-react-core';
import CreateOrAddToScheduleLineGroupIcon from '@mui/icons-material/CalendarMonth';
import TruckloadIcon from '@mui/icons-material/LocalShipping';
import { Box, Button, Tooltip } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { getRanges, notNullish } from '@paragon/utilities';

import { showCreateOrAddToScheduleLineGroupDialog, showCreateTruckloadDialog } from '../../../actions/dialogActions';
import { getScheduleGridCheckedBundleGuids, getScheduleGridRows } from '../../../selectors/scheduleGrid';
import { isGroupedProductionComponentInstanceRow } from '../../../selectors/scheduleGroupGrid';
import { RootState } from '../../../store';
import { collapseRows } from '../../../store/scheduleGridSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CollapseAllButton } from '../CollapseAllButton';
import { DownloadProductionPackageButton } from '../DownloadProductionPackageButton';
import { SetStatus } from '../SetStatus';

export function ScheduleGridToolbarPlugin() {
  const dispatch = useAppDispatch();
  const checkedBundleGuids = useAppSelector(getScheduleGridCheckedBundleGuids);

  const bundles = useAppSelector((state: RootState) => state.bundles);
  const truckloads = useAppSelector((state: RootState) => state.truckloads);
  const checkedBundles = checkedBundleGuids.map((bundleGuid) => bundles[bundleGuid]);
  const checkedBundlesBelongToMultipleScheduleGroups =
    uniq(checkedBundles.map((bundle) => bundle.scheduleGroupGuid)).length > 1;
  const checkedBundlesAlreadyAssignedToScheduleLineGroup = checkedBundles.filter(
    (bundle) => bundle.scheduleLineGroupGuid !== null,
  );
  const checkedBundlesAlreadyAssignedToTruckload = checkedBundles.filter((bundle) => bundle.truckloadGuid !== null);
  const checkedBundlesTruckloadIds = getRanges(
    uniq(checkedBundlesAlreadyAssignedToTruckload.map((bundle) => bundle.truckloadGuid))
      .map((truckloadGuid) => truckloads[truckloadGuid!]?.id)
      .filter(notNullish),
  );

  const scheduleGridRows = useAppSelector(getScheduleGridRows);
  const allExpandableOrCollapsableRowIds = scheduleGridRows
    .filter((row) => !isGroupedProductionComponentInstanceRow(row.rowId))
    .map((row) => row.rowId);

  return (
    <Plugin name="ScheduleGridToolbar" dependencies={[{ name: 'Toolbar' }]}>
      <Template name="toolbarContent">
        <Box sx={{ display: 'flex', flexDirection: 'row', verticalAlign: 'middle' }}>
          <Box sx={{ p: 1 }}>
            <Tooltip
              disableInteractive
              title={
                checkedBundleGuids.length === 0
                  ? 'Select bundles to create/add to schedule line group'
                  : checkedBundlesBelongToMultipleScheduleGroups
                    ? 'Select bundles from only one schedule group'
                    : checkedBundlesAlreadyAssignedToScheduleLineGroup.length > 0
                      ? `Bundle${checkedBundlesAlreadyAssignedToScheduleLineGroup.length > 1 ? 's' : ''} ${getRanges(
                          checkedBundlesAlreadyAssignedToScheduleLineGroup.map((bundle) => bundle.id),
                        )} ${
                          checkedBundlesAlreadyAssignedToScheduleLineGroup.length > 1 ? 'are' : 'is'
                        } already assigned to schedule line group(s)`
                      : 'Create/Add to schedule line group'
              }
            >
              <span>
                <Button
                  onClick={() => void dispatch(showCreateOrAddToScheduleLineGroupDialog())}
                  variant="outlined"
                  startIcon={<CreateOrAddToScheduleLineGroupIcon />}
                  disabled={
                    checkedBundleGuids.length === 0 ||
                    checkedBundlesBelongToMultipleScheduleGroups ||
                    checkedBundlesAlreadyAssignedToScheduleLineGroup.length > 0
                  }
                >
                  Schedule to line
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ width: 380, p: 1 }}>
            <SetStatus
              checkedBundleGuids={checkedBundleGuids}
              options={['Scheduled', 'StartedAssembly', 'FinishedAssembly', 'StoredOnYard', 'Shipped', 'Delivered']}
            />
          </Box>

          <Box sx={{ p: 1 }}>
            <DownloadProductionPackageButton
              bundleGuids={checkedBundleGuids}
              availableOutputTypes={[
                'ProductionAndShippingList',
                'CartLabels',
                'ComponentReport',
                'LumberPickList',
                'PlatePickList',
                'TpsFiles',
                'TreFiles',
                'TrsMachineryFiles',
              ]}
              productionArtifactsContext="ScheduleGroup"
            />
          </Box>

          <Box sx={{ pt: 1 }}>
            <TooltipIconButton
              title={
                checkedBundleGuids.length === 0
                  ? 'Select bundles to create a truckload'
                  : checkedBundlesAlreadyAssignedToTruckload.length > 0
                    ? `Bundle${checkedBundlesAlreadyAssignedToTruckload.length > 1 ? 's' : ''} ${getRanges(
                        checkedBundlesAlreadyAssignedToTruckload.map((bundle) => bundle.id),
                      ).join(', ')} ${
                        checkedBundlesAlreadyAssignedToTruckload.length > 1 ? 'are' : 'is'
                      } already assigned to truckload(s)${
                        checkedBundlesTruckloadIds.length > 0 ? ` ${checkedBundlesTruckloadIds.join(', ')}` : ''
                      }`
                    : 'Create truckload'
              }
              onClick={() => void dispatch(showCreateTruckloadDialog(checkedBundleGuids))}
              disabled={checkedBundleGuids.length === 0 || checkedBundlesAlreadyAssignedToTruckload.length > 0}
            >
              <TruckloadIcon />
            </TooltipIconButton>
          </Box>
        </Box>
        <TemplatePlaceholder />
        <CollapseAllButton
          collapseAll={(rowIds) => dispatch(collapseRows(rowIds))}
          allCollapsableRowIds={allExpandableOrCollapsableRowIds}
        />
      </Template>
    </Plugin>
  );
}
